/**
 * Streaming manifest loader - reads frames_index before full JSON completes
 * Enables fast animation startup without waiting for full manifest download
 */

import * as pako from 'pako'

export interface ManifestStreamingResult {
  framesIndex: string[];
  frameCount: number;
  loadTimeMs: number;
}

/**
 * Load manifest - tries .gz first, falls back to .json
 * Supports both compressed Azure deployment and uncompressed local testing
 * 
 * @param manifestUrl - URL to manifest.json (will try .gz first)
 * @returns framesIndex array and metadata
 */
export async function loadManifestStreaming(
  manifestUrl: string
): Promise<ManifestStreamingResult> {
  const startTime = performance.now()
  
  try {
    // Try .gz first (Azure deployment)
    const gzUrl = manifestUrl.endsWith('.gz') ? manifestUrl : manifestUrl + '.gz'
    let manifest: any = null
    let loadedFromGz = false
    
    const gzResponse = await fetch(gzUrl)
    if (gzResponse.ok) {
      // Successfully loaded .gz file - check if it's actually gzipped or HTML error
      const arrayBuffer = await gzResponse.arrayBuffer()
      
      // Check if response is HTML error (some servers return 404 HTML with 200 status)
      const textSample = new TextDecoder().decode(arrayBuffer.slice(0, 200))
      if (textSample.includes('<!doctype') || textSample.includes('<html') || textSample.includes('<!DOCTYPE')) {
        // Server returned HTML error page, try .json instead
        console.log(`⚠️ HTML error returned from .gz, trying .json...`)
        const jsonUrl = manifestUrl.endsWith('.gz') ? manifestUrl.slice(0, -3) : manifestUrl
        const jsonResponse = await fetch(jsonUrl)
        
        if (!jsonResponse.ok) {
          throw new Error(`Failed to load manifest: ${jsonResponse.status}`)
        }
        
        manifest = await jsonResponse.json()
        console.log(`✅ Loaded manifest from ${jsonUrl}`)
      } else {
        // Check if it's actually gzipped
        const firstBytes = new Uint8Array(arrayBuffer.slice(0, 2))
        const isGzipped = firstBytes[0] === 0x1f && firstBytes[1] === 0x8b
        
        if (isGzipped) {
          // File is gzip-compressed, decompress it
          const decompressed = pako.inflate(arrayBuffer, { to: 'string' })
          manifest = JSON.parse(decompressed)
        } else {
          // File is plain JSON, parse directly
          const jsonString = new TextDecoder().decode(arrayBuffer)
          manifest = JSON.parse(jsonString)
        }
        loadedFromGz = true
        console.log(`✅ Loaded manifest from ${gzUrl} (gzipped: ${isGzipped})`)
      }
    } else if (gzResponse.status === 404) {
      // .gz not found, try .json
      console.log(`⚠️ .gz not found (${gzResponse.status}), trying .json...`)
      const jsonUrl = manifestUrl.endsWith('.gz') ? manifestUrl.slice(0, -3) : manifestUrl
      const jsonResponse = await fetch(jsonUrl)
      
      if (!jsonResponse.ok) {
        throw new Error(`Failed to load manifest: ${jsonResponse.status}`)
      }
      
      manifest = await jsonResponse.json()
      console.log(`✅ Loaded manifest from ${jsonUrl}`)
    } else {
      throw new Error(`Failed to load manifest: ${gzResponse.status}`)
    }
    
    if (!manifest.frames_index || !Array.isArray(manifest.frames_index)) {
      throw new Error('Invalid manifest: missing frames_index array')
    }
    
    const loadTimeMs = performance.now() - startTime
    
    return {
      framesIndex: manifest.frames_index,
      frameCount: manifest.frames_index.length,
      loadTimeMs: Math.round(loadTimeMs)
    }
  } catch (error) {
    console.error('❌ Error loading manifest:', error)
    throw error
  }
}

/**
 * Build full frame URLs from frames_index array
 * 
 * @param framesIndex - Array of frame filenames from manifest
 * @param basePath - Base path to frames directory (e.g., /reconstructions/shop/section_0/frames)
 * @param shopId - Shop ID for query parameter
 * @returns Array of full frame URLs
 */
export function buildFrameUrls(
  framesIndex: string[],
  basePath: string,
  shopId: string
): string[] {
  return framesIndex.map(filename => 
    `${basePath}/${filename}?shop=${shopId}`
  )
}
